import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../lib/api';

export const useWebSocket = (conversationId) => {
    const { user } = useAuth();
    const token = localStorage.getItem('token');
    const socketRef = useRef(null);
    const [lastMessage, setLastMessage] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState(null);

    const connect = useCallback(() => {
        if (!conversationId || !token) return;

        // Construct WebSocket URL
        // Replace http/https with ws/wss and ensure /api prefix is present
        const baseUrl = API_URL.replace('http', 'ws');
        const wsUrl = `${baseUrl}/api/conversations/${conversationId}/ws?token=${token}`;

        // If we were using a token for auth, we might need to pass it in query params
        // But verify if your backend expects it. Current backend endpoint doesn't strictly check auth in the WS endpoint args
        // but typically you'd want to secure it. 
        // For now, mirroring the backend implementation which just takes conv_id.

        const socket = new WebSocket(wsUrl);

        socket.onopen = () => {
            console.log('WebSocket Connected');
            setIsConnected(true);
            setError(null);
        };

        socket.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                setLastMessage(data);
            } catch (error) {
                console.error('WebSocket message parse error:', error);
            }
        };

        socket.onclose = () => {
            console.log('WebSocket Disconnected');
            setIsConnected(false);
            // Simple reconnect logic could go here
        };

        socket.onerror = (event) => {
            console.error('WebSocket Error:', event);
            setError('Connection failed');
            setIsConnected(false);
        };

        socketRef.current = socket;

        return () => {
            socket.close();
        };
    }, [conversationId, token]);

    useEffect(() => {
        const cleanup = connect();
        return cleanup;
    }, [connect]);

    const sendMessage = useCallback((data) => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify(data));
        }
    }, []);

    return { lastMessage, isConnected, error, sendMessage };
};
